import { RouteRecordRaw } from 'vue-router';
import { isViewportBelow } from '@/theme/breakpoints';

export const routes: RouteRecordRaw[] = [
  {
    path: '/templates',
    name: 'TemplatesRoot',
    component: () => import('@/pages/template/TemplatesRoot.vue'),
    children: [
      {
        path: '',
        name: 'Templates',
        component: () => import('@/pages/template/TemplateListPage.vue'),
      },
      {
        path: ':templateId',
        meta: { paramPositions: [2] },
        name: 'TemplateRoot',
        component: () => import('@/pages/template/TemplateRoot.vue'),
        children: [
          {
            path: '',
            name: 'ProjectMapTemplateMap',
            component: () => import('@/pages/common/RemapPage.vue'),
            meta: { hideTopNavigation: true },
            beforeEnter: (to, from, next) => {
              if (isViewportBelow('map')) {
                next({ name: 'TemplateListView', params: to.params });
              } else {
                next();
              }
            },
          },
          {
            path: 'list',
            name: 'TemplateListView',
            component: () =>
              import('@/pages/template/TemplateListViewPage.vue'),
          },
          {
            path: 'milestones',
            name: 'TemplateMilestoneRoot',
            component: () =>
              import('@/pages/template/TemplateMilestoneRoot.vue'),
            children: [
              {
                path: ':milestoneId',
                name: 'TemplateMilestone',
                component: () =>
                  import('@/pages/template/TemplateMilestonePage.vue'),
                children: [
                  {
                    path: '',
                    name: 'TemplateMilestoneDetail',
                    component: () =>
                      import(
                        '@/pages/template/TemplateMilestoneDetailPage.vue'
                      ),
                  },
                  {
                    path: 'approvers',
                    name: 'TemplateMilestoneApprovers',
                    component: () =>
                      import(
                        '@/pages/template/TemplateMilestoneApproversPage.vue'
                      ),
                  },
                  {
                    path: 'activities/optional',
                    name: 'TemplateMilestoneActivitiesRequired',
                    component: () =>
                      import(
                        '@/pages/template/TemplateMilestoneActivitiesPage.vue'
                      ),
                  },
                  {
                    path: 'activities/required',
                    name: 'TemplateMilestoneActivitiesOptional',
                    component: () =>
                      import(
                        '@/pages/template/TemplateMilestoneActivitiesPage.vue'
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: 'activities/:activityId',
            name: 'TemplateActivity',
            component: () =>
              import('@/pages/template/TemplateActivityPage.vue'),
            meta: { paramPositions: [2, 4] },
            children: [
              {
                path: '',
                name: 'TemplateActivityDetail',
                component: () =>
                  import('@/pages/template/TemplateActivityDetailPage.vue'),
              },
              {
                path: 'approvers',
                name: 'TemplateActivityApprovers',
                meta: { paramPositions: [2, 4] },
                // prettier-ignore
                component: () => import('@/pages/template/TemplateActivityApproversPage.vue'),
              },
              {
                path: 'completion-criteria',
                name: 'TemplateCriteria',
                meta: { paramPositions: [2, 4] },
                component: () =>
                  import('@/pages/template/TemplateCriteriaPage.vue'),
                children: [
                  {
                    path: '',
                    name: 'TemplateCriteriaDetail',
                    component: () =>
                      import('@/pages/template/TemplateCriteriaDetailPage.vue'),
                  },
                  {
                    path: ':completionCriterionId',
                    meta: { paramPositions: [2, 4, 6] },
                    name: 'TemplateCriterion',
                    // prettier-ignore
                    component: () => import('@/pages/template/TemplateCriterionPage.vue'),
                  },
                ],
              },
              {
                path: 'checklist-steps',
                name: 'TemplateActivityChecklistSteps',
                meta: { paramPositions: [2, 4] },
                component: () =>
                  // prettier-ignore
                  import('@/pages/template/TemplateActivityChecklistStepsPage.vue'),
              },
              {
                path: 'links-and-tools',
                name: 'TemplateActivityLinksAndTools',
                meta: { paramPositions: [2, 4] },
                component: () =>
                  import('@/pages/template/TemplateActivityLinksToolsPage.vue'),
              },
              {
                path: 'documents',
                name: 'TemplateActivityDocuments',
                meta: { paramPositions: [2, 4] },
                component: () =>
                  import('@/pages/template/TemplateActivityDocumentsPage.vue'),
              },
              {
                path: 'predecessors',
                name: 'TemplateActivityPredecessors',
                meta: { paramPositions: [2, 4] },
                // prettier-ignore
                component: () => import('@/pages/template/TemplateActivityPredecessorsPage.vue'),
              },
              {
                path: 'successors',
                name: 'TemplateActivitySuccessors',
                meta: { paramPositions: [2, 4] },
                component: () =>
                  import('@/pages/template/TemplateActivitySuccessorsPage.vue'),
              },
              {
                path: 'watchers',
                name: 'TemplateActivityWatchers',
                meta: { paramPositions: [2, 4] },
                component: () =>
                  import('@/pages/template/TemplateActivityWatchersPage.vue'),
              },
            ],
          },
          {
            path: 'search-results',
            name: 'TemplateSearchResults',
            component: () =>
              import('@/pages/project/SearchResultsDetailPage.vue'),
          },
          {
            path: 'settings',
            name: 'TemplateSettings',
            component: () =>
              import('@/pages/template/TemplateSettingsPage.vue'),
            children: [
              {
                path: '',
                name: 'TemplateSettingsMain',
                redirect: (from) => ({
                  name: 'TemplateFunctions',
                  params: from.params,
                }),
              },
              {
                path: 'functions',
                name: 'TemplateFunctions',
                component: () =>
                  import('@/pages/template/TemplateSettingsFunctionsPage.vue'),
              },
              {
                path: 'links-and-tools',
                name: 'TemplateLinksAndTools',
                component: () =>
                  import(
                    '@/pages/template/TemplateSettingsLinksAndToolsPage.vue'
                  ),
              },
              {
                path: 'documents',
                name: 'TemplateDocuments',
                component: () =>
                  import('@/pages/template/TemplateSettingsDocumentsPage.vue'),
              },
              {
                path: 'quality-checks',
                name: 'TemplateQualityChecks',
                component: () =>
                  import(
                    '@/pages/template/TemplateSettingsQualityChecksPage.vue'
                  ),
              },
              {
                path: 'editors',
                name: 'TemplateEditors',
                meta: { paramPositions: [2] },
                component: () =>
                  import('@/pages/template/TemplateEditorPage.vue'),
              },
            ],
          },
        ],
      },
    ],
  },
];
